import React, { memo, useCallback, useMemo } from 'react'
import { Form, Formik } from 'formik'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { OatfiBusiness, OatfiBusinessFormFields } from 'src/interfaces/oatfiBusiness'
import { BusinessProfileFormContent } from './BusinessProfileFormContent'
import { businessProfileFormValidator } from './BusinessProfileForm.validator'
import { toFormValues } from './BusinessProfileForm.mapper'

interface Props {
  business: OatfiBusiness | null
  isReadOnly: boolean
  isSaving: boolean
  onSubmit(values: OatfiBusinessFormFields): void
}

const BusinessProfileFormComponent = ({ business, isReadOnly, isSaving, onSubmit }: Props) => {
  const initialValues = useMemo(() => {
    return toFormValues(business)
  }, [business])

  const handleSubmit = useCallback(
    (values: OatfiBusinessFormFields) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  return (
    <Formik<OatfiBusinessFormFields>
      initialValues={initialValues}
      validationSchema={businessProfileFormValidator}
      onSubmit={handleSubmit}
      // Reinitialises from the saved business after every successful save. `PUT` takes the same
      // non-partial DTO as `POST`, so the form must always hold the complete current record.
      enableReinitialize
    >
      <Form>
        <SectionCard
          title="Oatfi business"
          subtitle={
            business === null
              ? 'No business has been created for this customer yet.'
              : 'The business record the lender underwrites.'
          }
          dataTestId="admin-customer-business-profile-card"
        >
          <BusinessProfileFormContent
            mode={business === null ? 'create' : 'update'}
            isReadOnly={isReadOnly}
            isSaving={isSaving}
            isBankAccountLinked={Boolean(business?.plaidToken)}
          />
        </SectionCard>
      </Form>
    </Formik>
  )
}

export const BusinessProfileForm = memo(BusinessProfileFormComponent)
